import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LayoutGrid, LogOut } from "lucide-react";
import Logo from "./Logo";
import LanguageToggle from "./LanguageToggle";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t.nav.problem, href: "/#problem" },
    { label: t.nav.idea, href: "/#idea" },
    { label: t.nav.proof, href: "/#proof" },
    { label: t.nav.vision, href: "/#vision" },
  ];

  const handleLogout = () => {
    navigate("/");
    logout();
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3 backdrop-blur-xl bg-white/80 border-b border-slate-200/70" : "py-5 bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
        <Logo size={42} />

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("/#", "")}`}
              className="text-sm font-medium text-slate-600 hover:text-u-ink transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/contact"
            data-testid="nav-link-contact"
            className="text-sm font-medium text-slate-600 hover:text-u-ink transition-colors"
          >
            {t.nav.contact}
          </Link>
          <a
            href="/investor.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-link-investors"
            className="text-sm font-medium text-slate-600 hover:text-u-ink transition-colors"
          >
            {t.nav.investors}
          </a>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-btn"
                className="inline-flex items-center gap-2 rounded-full bg-u-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-u-ink/90 transition-all"
              >
                <LayoutGrid size={15} /> {t.nav.dashboard}
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-u-ink hover:border-slate-300 transition-all"
              >
                <LogOut size={15} />
              </button>
            </>
          ) : (
            <>
              <a
                href="https://use1id-prd.vercel.app/r/logon"
                data-testid="nav-login-btn"
                className="text-sm font-medium text-slate-700 hover:text-u-ink px-3 transition-colors"
              >
                {t.nav.login}
              </a>
              <a
                href="https://use1id-prd.vercel.app/r/register"
                data-testid="nav-getstarted-btn"
                className="rounded-full bg-u-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-u-ink/90 transition-all"
              >
                {t.nav.getStarted}
              </a>
            </>
          )}
        </div>

        <button
          className="lg:hidden text-u-ink"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden mt-3 mx-4 rounded-2xl card p-5 flex flex-col gap-4" data-testid="mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-base font-medium text-slate-700"
            >
              {l.label}
            </a>
          ))}
          <Link to="/contact" onClick={() => setOpen(false)} className="text-base font-medium text-slate-700">
            {t.nav.contact}
          </Link>
          <a
            href="/investor.html"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-link-investors"
            className="text-base font-medium text-slate-700"
          >
            {t.nav.investors}
          </a>
          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <LanguageToggle />
            {user ? (
              <div className="flex gap-2">
                <Link to="/dashboard" onClick={() => setOpen(false)} className="rounded-full bg-u-ink px-4 py-2 text-sm font-semibold text-white">
                  {t.nav.dashboard}
                </Link>
                <button onClick={handleLogout} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-u-ink">
                  {t.nav.logout}
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <a href="https://use1id-prd.vercel.app/r/logon" onClick={() => setOpen(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-u-ink">
                  {t.nav.login}
                </a>
                <a href="https://use1id-prd.vercel.app/r/register" onClick={() => setOpen(false)} className="rounded-full bg-u-ink px-4 py-2 text-sm font-semibold text-white">
                  {t.nav.getStarted}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
